import { useState } from 'react'
import { Alert, Button, Card, Group, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import backend from '../../backend/backend'
import DataList from '../../components/shared/DataList'
import PageHeader from '../../components/shared/PageHeader'
import StatusBadge from '../../components/shared/StatusBadge'
import Money from '../../components/shared/Money'
import { useAction, useCan, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadCustomer, loadCustomers } from '../../store/dataActionCreators'
import { displayPhone, formatDate, fullName, maskedId } from '../../utils/format'

export function CustomersList({ base }) {
  const t = useT()
  const navigate = useNavigate()
  const canRegister = useCan('customers.register')
  const [q, setQ] = useState('')
  const [debounced] = useDebouncedValue(q, 300)
  const { data, loading } = useResource(keys.customers(debounced, 1), () => loadCustomers(debounced, 1))
  const columns = [
    { key: 'customer_no', header: 'col_customer_no', ref: true },
    { key: 'name', header: 'col_name', render: fullName },
    { key: 'phone', header: 'col_phone', render: (c) => displayPhone(c.phone) },
    { key: 'bvn', header: 'col_bvn', render: (c) => maskedId(c.bvn_last4), mobileHidden: true },
    { key: 'identity_status', header: 'col_identity', render: (c) => <StatusBadge status={c.identity_status === 'pending' ? 'pending' : c.identity_status} /> },
  ]
  return (
    <>
      <PageHeader
        title="customers_title"
        actions={
          canRegister && (
            <Button component={Link} to={`${base}/customers/new`}>
              <Message id="new_customer" />
            </Button>
          )
        }
      />
      <TextInput mb="md" placeholder={t('customers_search')} value={q} onChange={(e) => setQ(e.currentTarget.value)} />
      <DataList items={data?.items} columns={columns} loading={loading} empty="customers_empty" onRowClick={(c) => navigate(`${base}/customers/${c.id}`)} />
    </>
  )
}

const NG_STATES = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara']

export function CustomerForm({ base }) {
  const t = useT()
  const navigate = useNavigate()
  const config = useSelector((s) => s.auth.config)
  const { busy, run } = useAction()
  const [fieldErrors, setFieldErrors] = useState({})
  const form = useForm({
    initialValues: { first_name: '', last_name: '', phone: '', alt_phone: '', email: '', date_of_birth: '', gender: null, address_line: '', city: '', state: null, occupation: '', kin_name: '', kin_phone: '', kin_relationship: '', bvn: '', nin: '' },
    validate: {
      first_name: (v) => (v.trim() ? null : t('error_required')),
      last_name: (v) => (v.trim() ? null : t('error_required')),
      phone: (v) => (v.replace(/\D/g, '').length >= 10 ? null : t('error_phone')),
      bvn: (v) => (!v || /^\d{11}$/.test(v.replace(/\s/g, '')) ? null : t('error_11_digits')),
      nin: (v) => (!v || /^\d{11}$/.test(v.replace(/\s/g, '')) ? null : t('error_11_digits')),
    },
  })
  const blocked = config && config.encryptionConfigured === false
  const submit = async (values) => {
    const body = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '' && v !== null))
    setFieldErrors({})
    const created = await run(() => backend.createCustomer(body).catch((err) => {
      if (err?.fields) setFieldErrors(err.fields)
      throw err
    }), { success: 'customer_saved' })
    if (created) navigate(`${base}/customers/${created.id}`)
  }
  const input = (name, label, props = {}) => <TextInput label={t(label)} {...form.getInputProps(name)} error={form.errors[name] || fieldErrors[name]} {...props} />
  return (
    <>
      <PageHeader title="new_customer" />
      {blocked && (
        <Alert color="orange" mb="md">
          <Message id="config_encryption" />
        </Alert>
      )}
      <form onSubmit={form.onSubmit(submit)} noValidate>
        <Stack maw={720}>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {input('first_name', 'field_first_name')}
            {input('last_name', 'field_last_name')}
            {input('phone', 'field_phone', { type: 'tel', inputMode: 'tel' })}
            {input('alt_phone', 'field_alt_phone', { type: 'tel', inputMode: 'tel' })}
            {input('email', 'field_email', { type: 'email' })}
            {input('date_of_birth', 'field_dob', { type: 'date' })}
            <Select label={t('field_gender')} data={[{ value: 'female', label: t('gender_female') }, { value: 'male', label: t('gender_male') }, { value: 'other', label: t('gender_other') }]} {...form.getInputProps('gender')} clearable />
            {input('occupation', 'field_occupation')}
          </SimpleGrid>
          {input('address_line', 'field_address')}
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {input('city', 'field_city')}
            <Select label={t('field_state')} data={NG_STATES} searchable {...form.getInputProps('state')} />
          </SimpleGrid>
          <Title order={4}>
            <Message id="section_kin" />
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            {input('kin_name', 'field_kin_name')}
            {input('kin_phone', 'field_kin_phone', { type: 'tel', inputMode: 'tel' })}
            {input('kin_relationship', 'field_kin_relationship')}
          </SimpleGrid>
          <Title order={4}>
            <Message id="section_identity" />
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {input('bvn', 'field_bvn', { inputMode: 'numeric', maxLength: 11, autoComplete: 'off' })}
            {input('nin', 'field_nin', { inputMode: 'numeric', maxLength: 11, autoComplete: 'off' })}
          </SimpleGrid>
          <Group>
            <Button type="submit" loading={busy}>
              <Message id="save" />
            </Button>
            <Button variant="default" onClick={() => navigate(-1)}>
              <Message id="cancel" />
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  )
}

export function CustomerDetail({ base }) {
  const { id } = useParams()
  const { data, reload } = useResource(keys.customer(id), () => loadCustomer(id))
  const canReveal = useCan('customers.reveal_ids')
  const canVerify = useCan('customers.verify_identity')
  const { busy, run } = useAction()
  const [revealed, setRevealed] = useState(null)
  const navigate = useNavigate()
  if (!data) return null
  const c = data.customer
  const row = (label, value) => (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Text size="sm" c="dimmed">
        <Message id={label} />
      </Text>
      <Text size="sm" ta="right">
        {value || '—'}
      </Text>
    </Group>
  )
  return (
    <>
      <PageHeader
        title="customer_profile"
        subtitle={`${fullName(c)} · ${c.customer_no}`}
        actions={
          base === '/agent' && (
            <Button component={Link} to={`/agent/applications/new?customerId=${c.id}`}>
              <Message id="new_application" />
            </Button>
          )
        }
      />
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
        <Card>
          <Stack gap={6}>
            {row('field_phone', displayPhone(c.phone))}
            {row('field_email', c.email)}
            {row('field_dob', formatDate(c.date_of_birth))}
            {row('field_address', [c.address_line, c.city, c.state].filter(Boolean).join(', '))}
            {row('field_occupation', c.occupation)}
            {row('field_kin_name', [c.kin_name, displayPhone(c.kin_phone)].filter((x) => x && x !== '—').join(' · '))}
          </Stack>
        </Card>
        <Card>
          <Stack gap={6}>
            {row('field_bvn', revealed ? revealed.bvn : maskedId(c.bvn_last4))}
            {row('field_nin', revealed ? revealed.nin : maskedId(c.nin_last4))}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                <Message id="col_identity" />
              </Text>
              <StatusBadge status={c.identity_status} />
            </Group>
            <Group>
              {canReveal && !revealed && (c.has_bvn || c.has_nin) && (
                <Button size="xs" variant="default" loading={busy} onClick={() => run(() => backend.revealIds(c.id), { onDone: setRevealed })}>
                  <Message id="reveal_ids" />
                </Button>
              )}
              {canVerify && c.identity_status !== 'verified' && (
                <Button size="xs" loading={busy} onClick={() => run(() => backend.verifyIdentity(c.id, 'verified'), { success: 'identity_verified', onDone: reload })}>
                  <Message id="verify_identity" />
                </Button>
              )}
            </Group>
          </Stack>
        </Card>
      </SimpleGrid>
      <Title order={4} mb="sm">
        <Message id="nav_applications" />
      </Title>
      <DataList
        items={data.applications}
        empty="applications_empty"
        onRowClick={(a) => navigate(`${base}/applications/${a.id}`)}
        columns={[
          { key: 'application_no', header: 'col_ref', ref: true },
          { key: 'product_name', header: 'col_product' },
          { key: 'principal_kobo', header: 'col_financed', render: (a) => <Money kobo={a.principal_kobo} /> },
          { key: 'status', header: 'col_status', render: (a) => <StatusBadge status={a.status} /> },
        ]}
      />
      <Title order={4} mt="lg" mb="sm">
        <Message id="nav_loans" />
      </Title>
      <DataList
        items={data.loans}
        empty="loans_empty"
        onRowClick={(l) => navigate(`${base}/loans/${l.id}`)}
        columns={[
          { key: 'loan_no', header: 'col_ref', ref: true },
          { key: 'outstanding_kobo', header: 'col_outstanding', render: (l) => <Money kobo={l.outstanding_kobo} /> },
          { key: 'next_due_date', header: 'col_next_due', render: (l) => formatDate(l.next_due_date) },
          { key: 'status', header: 'col_status', render: (l) => <StatusBadge status={l.status} /> },
        ]}
      />
      <Title order={4} mt="lg" mb="sm">
        <Message id="documents_title" />
      </Title>
      <DataList
        items={data.documents}
        empty="documents_empty"
        columns={[
          { key: 'doc_type', header: 'col_type', render: (d) => <Message id={`doc_${d.doc_type}`} /> },
          { key: 'original_filename', header: 'col_file', render: (d) => <a href={backend.documentUrl(d.id)} target="_blank" rel="noreferrer">{d.original_filename}</a> },
          { key: 'status', header: 'col_status', render: (d) => <StatusBadge status={d.status} /> },
        ]}
      />
    </>
  )
}
